import { CircularProgress } from "@mui/material";
import { useMotionValue } from "motion/react";
import { FC, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { FloatingMenuStyled, useFloatMenu } from "@ryde/controls";
import { bookingCalendarSelectedCellsCount, useIsMobile } from "@ryde/shared";

import { BookingsFloatingMenuCalendarInner } from "./bookings.floating-menu.calendar.inner";
import { BookingCalendarFloatingSuspense } from "./bookings.panel.styled";

export const BookingsFloatingMenuCalendar: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  const selectedCellsCount = useRecoilValue(bookingCalendarSelectedCellsCount);
  const isOpened = Boolean(selectedCellsCount);

  const {
    isInitFinished,
    leftX,
    bottomY,
    setLeftX,
    setBottomY,
    floatingMenuRef,
    setIsAllowedInitCentering,
  } = useFloatMenu({ page: "bookingsCalendar", isOpened });
  const left = useMotionValue(leftX);
  const bottom = useMotionValue(bottomY);

  const onDragEndHandler = useEvent((): void => {
    setLeftX(left.get());
    setBottomY(bottom.get());
  });

  const onRender = useEvent(() => {
    setIsAllowedInitCentering(true);
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    left.set(leftX);
  }, [isOpened, left, leftX]);

  return (
    isOpened && (
      <FloatingMenuStyled
        ref={floatingMenuRef}
        drag
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={onDragEndHandler}
        style={{
          x: left,
          bottom,
          opacity: !isInitFinished ? 0 : "initial",
        }}
      >
        {!isMobile && <SVG_MUI.GripVertical3SVG className="pointer grip" />}
        <Suspense
          fallback={
            <BookingCalendarFloatingSuspense>
              <span className="loading_text">
                {t("booking.bulk.calculatingBookings")}
              </span>
              <div>
                <CircularProgress color="primary" disableShrink />
              </div>
            </BookingCalendarFloatingSuspense>
          }
        >
          <BookingsFloatingMenuCalendarInner onRender={onRender} />
        </Suspense>
      </FloatingMenuStyled>
    )
  );
};
